import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useModulesSummary } from '@/api/dashboard';
import {
  FixedNav,
  Caption,
  Card,
  Button,
  Divider,
  StatStrip,
  AnimatedNumber,
  PulseDot,
  OnboardingModal,
  MODULE_SLIDES,
  TACTICS_THEME_LABEL_FR,
} from '@/ui';
import { tacticsApi } from './api';

/**
 * Tactics Dojo — liste des thèmes (port V2).
 *
 * Source proto : `docs/prototypes/ui-kit-v2/ui_kits/the_chess_coach/TacticsDojo.jsx`
 * §TacticsList. Wiring : `useQuery(['tactics', 'themes'])` (existant MVP1.1)
 * + `useModulesSummary(30).modules.tactic.mastery_pct` pour la moyenne globale.
 *
 * Choix design vs proto :
 *  - Le proto a `THEMES_PROGRESS` hardcodé par thème → mock retiré, on affiche
 *    `—` pour mastery par thème en attendant un endpoint dédié (T2 KPI nocturnes).
 *    PRODUCT §5.4 + CLAUDE §1 : pas de contenu inventé en prod.
 *  - Le proto a `{ xp: 25 }` au validate → retiré (PRODUCT §5.4 anti-gamification).
 *  - Filter tabs "mastered / weak" retirés tant qu'on n'a pas le mastery par
 *    thème — un filter qui ne discrimine rien est trompeur.
 */

/**
 * Source unique des labels FR des thèmes Lichess : `TACTICS_THEME_LABEL_FR`
 * exporté par `@/ui/labels.ts`.
 */

const THEME_SAMPLE: Record<string, string> = {
  deflection: 'Force le défenseur à quitter sa fonction',
  decoy: 'Force une pièce sur une case fatale',
  discovered_attack: "Démasque l'attaque d'une pièce arrière",
  double_attack: 'Fourchette · attaque 2 cibles',
  pin: 'Immobilise une pièce devant son roi',
  skewer: 'Attaque alignée, gain matériel',
  x_ray: 'Pression à travers une pièce',
  zugzwang: "L'adversaire n'a que de mauvais coups",
  removing_defender: 'Capture la pièce qui défend',
  overloading: 'Une pièce défend trop de choses',
};

export function TacticsListPage(): JSX.Element {
  const navigate = useNavigate();
  const themesQ = useQuery({
    queryKey: ['tactics', 'themes'],
    queryFn: () => tacticsApi.themes(),
  });
  const modulesQ = useModulesSummary(30);

  const totalPuzzles = useMemo(
    () => (themesQ.data?.themes ?? []).reduce((sum, t) => sum + t.n, 0),
    [themesQ.data],
  );
  const avgMastery = Math.round(modulesQ.data?.modules.tactic.mastery_pct ?? 0);

  const chips = [
    { label: 'Thèmes', value: String(themesQ.data?.count ?? 0), color: 'var(--cyan-500)' },
    { label: 'Puzzles', value: String(totalPuzzles), color: 'var(--paper-50)' },
  ];

  return (
    <>
      {/* T7 MVP2.5 — onboarding 1ère visite Tactics Dojo (bug A8). */}
      <OnboardingModal
        moduleSlug="tactics-dojo"
        moduleLabel="Tactics Dojo"
        tone="cyan"
        slides={MODULE_SLIDES['tactics-dojo'] ?? []}
      />
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--cyan-500)" size={8} />
            <Caption color="cyan">Tactics Dojo</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">Puzzles tactiques</h1>
          <p className="text-body-sm text-ink-300">
            {totalPuzzles} puzzles · seed Lichess Puzzle DB (rating{' '}
            <span className="font-mono text-paper-50">1900-2300</span>, popularité ≥80).
          </p>
        </header>

        {/* Stats strip */}
        <StatStrip className="flex-wrap items-stretch gap-x-8">
          <StatTile label="Thèmes" value={themesQ.data?.count ?? 0} color="var(--paper-50)" />
          <Divider vertical />
          <StatTile label="Puzzles" value={totalPuzzles} color="var(--cyan-500)" />
          <Divider vertical />
          <StatTile
            label="Maîtrise · 30j"
            value={avgMastery}
            color="var(--volt-500)"
            format={(v) => `${Math.round(v)}%`}
          />
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[180px]">
            <Caption>Source officielle</Caption>
            <span className="font-mono text-[11px] leading-snug text-ink-300">
              Lichess Puzzle Database
              <br />
              <span className="text-paper-50">25</span> max / thème
            </span>
          </div>
          <div className="ml-auto self-center">
            <Button
              variant="primary"
              accent="cyan"
              onClick={() => navigate('/tactics-dojo/drill')}
            >
              ⚡ Exercice aléatoire
            </Button>
          </div>
        </StatStrip>

        {themesQ.isPending && (
          <Card>
            <p className="text-ink-300 italic">Chargement des thèmes…</p>
          </Card>
        )}
        {themesQ.isError && (
          <Card>
            <p className="text-coral-500">
              Aucun puzzle. Préviens Valentin (la base est vide).
            </p>
          </Card>
        )}

        {themesQ.data && (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0">
            {themesQ.data.themes.map((t) => (
              <li key={t.theme}>
                <ThemeTile
                  count={t.n}
                  label={TACTICS_THEME_LABEL_FR[t.theme] ?? t.theme}
                  {...(THEME_SAMPLE[t.theme] ? { sample: THEME_SAMPLE[t.theme]! } : {})}
                  onClick={() =>
                    navigate(`/tactics-dojo/drill?theme=${encodeURIComponent(t.theme)}`)
                  }
                />
              </li>
            ))}
          </ul>
        )}

        <footer className="pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Mission Control
          </button>
        </footer>
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  color,
  format,
}: {
  label: string;
  value: number;
  color: string;
  format?: (v: number) => string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-[80px]">
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        {...(format ? { format } : {})}
        className="font-mono"
        style={{ fontSize: 26, color, lineHeight: 1, fontWeight: 500 }}
      />
    </div>
  );
}

function ThemeTile({
  count,
  label,
  sample,
  onClick,
}: {
  count: number;
  label: string;
  sample?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left flex flex-col gap-3 p-5 bg-ink-800 border border-ink-700 rounded-xl transition-all duration-150 hover:border-cyan-500/50 hover:shadow-card-hover animate-card-enter w-full"
    >
      <div className="flex justify-between items-center">
        <Caption color="cyan">{count} puzzles</Caption>
      </div>
      <div>
        <h2 className="font-display text-heading-3 text-paper-50">{label}</h2>
        {sample && (
          <p className="mt-1.5 text-body-sm text-ink-300 italic leading-snug">{sample}</p>
        )}
      </div>
      <div className="mt-auto flex flex-col gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex-1 h-1 bg-ink-700 rounded-full overflow-hidden" />
          <span
            className="font-mono text-[11px] text-ink-300 tabular-nums w-9 text-right"
            title="Maîtrise par thème : bientôt disponible"
          >
            —%
          </span>
        </div>
        <div className="flex justify-between items-center font-mono text-[11px] text-ink-300">
          <span>maîtrise</span>
          <span className="text-cyan-500">Exercice →</span>
        </div>
      </div>
    </button>
  );
}
